import { useGetProfileForAllQuery } from '@api/front-office/profile.api.rtk';
import { useLazyGetAllPropertiesQuery } from '@api/property.api.rtk';
import useDisclosure from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import POSConfirmationDialog from '@modules/point-of-sale/components/pos-selector/pos-confirmation-dialog';
import { selectPOSisHaveOrders } from '@modules/point-of-sale/slice/pos-selector';
import { RefreshOutlined, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { selectDepartmentListCode } from '@store/selector/property.selector';
import { moduleArr } from '@utils/module-arr';
import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import useAppContext from '../../hooks/useAppContext';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  PropertyState,
  setIsStraightToModule,
  setPropertiesState,
  setSelectedProperty,
  setSelfRegistrationProperties,
  setSortedProperties,
} from '../../store/slices/property.slice';
import { selectSessionExpired } from '@store/selector/api.selector';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { setPOSSelectedStationSlice } from '@modules/point-of-sale/slice/pos-stations.slice';
import { generalSettingsAPI } from '@api/pos/pos-bo-general-settings.api.rtk';

const currentModuleLink = (currentModule: string) => {
  const module = moduleArr.find((m) => m.code === currentModule);
  return module ? module.link : '';
};

export const useProperty = ({
  disableAllProperties,
  initialValue,
}: {
  disableAllProperties?: boolean;
  initialValue?: any;
}) => {
  const dispatch = useAppDispatch();
  const sortedProperties = useAppSelector(
    (state) => state.properties.sortedProperties,
  );
  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );
  const { toast } = useToast();
  const [getAllProperties, { isLoading, isError: isErrorApi }] =
    useLazyGetAllPropertiesQuery();
  const isSessionExpired = useAppSelector(selectSessionExpired);
  const { startLoading, stopLoading } = useAppContext();
  const [property, setProperty] = useState<{ label: string; value: string }>(
    initialValue || selectedProperty,
  );

  const [properties, setProperties] = useState<
    { label: string; value: string; activeModule: string[] }[]
  >(
    sortedProperties?.length >= 1
      ? [
          ...sortedProperties,
          !disableAllProperties
            ? {
                label: 'All Properties',
                value: 'All Properties',
                activeModule: [],
              }
            : { label: '', value: '', activeModule: [] },
        ]?.filter((item) => item.value !== '')
      : sortedProperties || [],
  );

  /**
   * Fetches all properties from the API and updates the state
   * @param {boolean} [setCurrentData] - Flag to determine if current data should be set
   */
  const getProperties = async (setCurrentData?: boolean) => {
    startLoading();
    const {
      data: newProperties,
      isError,
      error,
      isSuccess,
    } = await getAllProperties(null);

    if (
      (newProperties && setCurrentData) ||
      (newProperties && isSuccess && !isSessionExpired)
    ) {
      // CHANGE WHEN UAT BRANCH
      const preNewPropertiesState = newProperties.data.properties;

      // const newPropertiesState = preNewPropertiesState.map(({ property }) => ({
      //   property: {
      //     ...property,
      //     taxInfo: {
      //       ...property.taxInfo,
      //       ...newProperties.data?.properties?.[1]?.find(
      //         ({ propertyID }) => propertyID === property.propertyID,
      //       ),
      //     },
      //   },
      // }));

      const properties = preNewPropertiesState?.map(
        ({ property }: { property: PropertyState }) => property,
      );
      dispatch(setPropertiesState(properties));

      const selfRegistration = properties?.map((property: PropertyState) => {
        return {
          propertyName: property?.propertyName,
          propertyID: property?.propertyID,
          selfRegistration: property?.selfRegistration,
        };
      });

      selfRegistration &&
        dispatch(setSelfRegistrationProperties(selfRegistration));

      const newData = properties
        ?.slice()
        .sort((a, b) =>
          (a.propertyName || '').localeCompare(
            b.propertyName || '',
            undefined,
            {
              sensitivity: 'base',
            },
          ),
        )
        .map((property: PropertyState) => ({
          label: property?.propertyName || 'N/A',
          value: property?.propertyID || 'N/A',
          activeModule:
            property?.userData?.Modules?.map(({ Module }) => {
              const currentModule = moduleArr.find(
                (item) => item.code === Module.Code,
              );

              return currentModule?.isActive
                ? currentModule?.link?.split('/')[1]
                : false;
            })?.filter(Boolean) ?? [],
        }));

      if (newData) dispatch(setSortedProperties(newData));

      if (newData?.length == 1) {
        setProperties([...newData]);
        initialValue?.label == '' && setProperty(newData[0]);
      } else {
        setProperties(
          [
            ...newData,
            !disableAllProperties
              ? {
                  label: 'All Properties',
                  value: 'All Properties',
                }
              : { label: '', value: '' },
          ].filter((item) => item?.value !== ''),
        );
        initialValue?.label == '' && setProperty(newData[0]);
      }
    }
    stopLoading();
    if (isError) {
      if (
        error?.toString() == '/validate-token' ||
        error?.toString()?.includes('/validate-token')
      )
        return;
      toast({ message: error, severity: 'error', errorLevel: 1 });
    }
  };

  return {
    getProperties,
    setProperty,
    isLoading,
    isError: isErrorApi,
    property,
    properties,
  };
};

/**
 * PropertySelector component
 *
 * This component renders an Autocomplete input field for selecting properties.
 * It fetches properties from an API, manages the selection state, and provides
 * functionality to refresh the property list.
 *
 * @param {Object} props - The component props
 * @param {CSSProperties} [props.sx] - Additional styles to apply to the component
 * @param {any} [props.initialValue] - Initial value for the property selector
 * @returns {JSX.Element} The rendered PropertySelector component
 */
export const PropertySelector = ({
  sx,
  initialValue,
  v2,
  disableAllProperties,
}: {
  sx?: CSSProperties;
  initialValue?: any;
  v2?: boolean;
  disableAllProperties?: boolean;
}) => {
  const {
    getProperties,
    properties,
    property: value,
    setProperty: setValue,
    isLoading,
  } = useProperty({
    disableAllProperties,
    initialValue,
  });

  const pathname = window.location.pathname?.split('/')?.[1];

  const visibleOptions = useMemo(() => {
    return properties
      .filter(({ activeModule, label }) => {
        if (!pathname || label === 'All Properties') return true;
        if (activeModule?.length === 0) return false;
        return activeModule?.includes(pathname);
      })
      .sort((a, b) => {
        if (a.label === 'All Properties') return 1;
        if (b.label === 'All Properties') return -1;

        return a.label.localeCompare(b.label);
      });
  }, [properties, pathname]);

  const dialogDisclosure = useDisclosure();
  const { startLoading, stopLoading } = useAppContext();
  const { textColor, white, bgColor, secondary } = getColor();
  const navigate = useNavigate();

  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );
  const currentModule = useCurrentModule();
  const isStraightToModule = useAppSelector(
    (state) => state.properties.isStraightToModule,
  );

  // #region POS CONFIG
  const isPOSModule = useMemo(() => pathname === 'point-of-sale', [pathname]);
  const isHaveOrders = useAppSelector((state) =>
    isPOSModule ? selectPOSisHaveOrders(state) : undefined,
  );
  const isShowPOSDialog = isPOSModule && isHaveOrders;
  // #endregion

  const modules = useAppSelector(selectDepartmentListCode);

  useGetProfileForAllQuery(selectedProperty.value, {
    skip: selectedProperty.value === 'All Properties',
  });

  const [inputValue, setInputValue] = useState('');
  const [initialStateValue, setInitialStateValue] = useState<{
    label: string;
    value: string;
  }>();

  const dispatch = useAppDispatch();

  const isMounted = useRef(true);

  useLayoutEffect(() => {
    if (!currentModule) return;

    const module = modules?.find(
      (item) => item.Code === currentModule && item.isActive,
    );

    if (!module && selectedProperty.value) {
      dispatch(setIsStraightToModule(true));
      return;
    }

    const link = currentModuleLink(currentModule);

    if (!link || isStraightToModule || !selectedProperty.value) return;

    dispatch(setIsStraightToModule(true));
    navigate(`${link}/dashboard`);
  }, [selectedProperty, currentModule, isStraightToModule, modules]);

  useEffect(() => {
    initialValue?.label != '' && setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isMounted.current) {
      dispatch(setSelectedProperty(value));
      dispatch(
        generalSettingsAPI.util.invalidateTags(['GeneralSettingsDetailsList']),
      );
    }
  }, [value]);

  useEffect(() => {
    if (properties?.length > 0 && window.location.pathname !== '/') return;
    (async () => {
      startLoading();
      await getProperties().finally(stopLoading);
    })();
    return () => {
      isMounted.current = false;
      setValue(selectedProperty);
    };
  }, []);

  /**
   * Renders the "No options" text with a refresh button
   * @returns {JSX.Element} The NoOptionsText component
   */
  const NoOptionsText = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'space-between',
      }}
    >
      <span>No properties found.</span>
      <IconButton
        onClick={async () => {
          startLoading();
          await getProperties(true).finally(stopLoading);
        }}
      >
        <RefreshOutlined />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Autocomplete
        className='property-selector'
        disableClearable
        loading={isLoading}
        value={value}
        inputValue={inputValue}
        options={visibleOptions}
        isOptionEqualToValue={(option, value) =>
          option.value === value.value || true
        }
        onChange={(_e: any, newValue) => {
          if (isShowPOSDialog) {
            setInitialStateValue(newValue);
            dialogDisclosure.onOpen();
          } else {
            isPOSModule && dispatch(setPOSSelectedStationSlice());
            setValue(newValue);
          }
        }}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => {
          const value: string = params.inputProps.value as string;
          return (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position='start' sx={{ paddingLeft: '5px' }}>
                    {!value ? (
                      <Search sx={{ color: white }} />
                    ) : (
                      <Avatar
                        sx={{
                          width: '24px',
                          height: '24px',
                          fontSize: '12px',
                          color: '#FFFFFF',
                          bgcolor:
                            value === 'All Properties' ? 'orange' : secondary,
                        }}
                      >
                        {value.split(' ').map((item) => item[0])}
                      </Avatar>
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end'>
                    {visibleOptions && (
                      <IconButton
                        sx={{
                          mr: 0.5,
                          bgcolor: 'transparent',
                          ':hover': { bgcolor: 'transparent' },
                        }}
                        onClick={async () => {
                          startLoading();
                          await getProperties().finally(stopLoading);
                        }}
                      >
                        <RefreshOutlined
                          sx={{
                            fontSize: '18px',
                            color: '#161C248F!important',
                          }}
                        />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </InputAdornment>
                ),
              }}
              placeholder={isLoading ? 'Loading...' : 'Search Property'}
              sx={{
                '& input::placeholder': {
                  opacity: 1,
                },
                bgcolor: v2 ? bgColor : undefined,
                borderRadius: 5000,
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              key={option.value}
              component='li'
              sx={{ gap: '20px' }}
              {...rest}
              onClick={(e) => {
                props.onClick?.(e);
              }}
            >
              <Avatar
                sx={{
                  width: '24px',
                  height: '24px',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  bgcolor:
                    option.label === 'All Properties' ? 'orange' : secondary,
                }}
              >
                {...option.label.split(' ').map((item) => item[0])}
              </Avatar>
              {option.label}
            </Box>
          );
        }}
        sx={[
          {
            width: '100%',
            maxWidth: '550px',
            bgcolor: white,
            borderRadius: '28px',
            '.MuiInputBase-input': {
              cursor: 'pointer',
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: white,
                borderRadius: '24px',
              },
              '&:hover fieldset': {
                borderColor: textColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: secondary,
              },
              '& .MuiSvgIcon-root': {
                color: textColor,
              },
              '& .MuiAutocomplete-endAdornment': {
                display: 'flex',
                gap: '10px',
                paddingRight: '5px',
                border: 0,
              },
              '& .MuiAutocomplete-endAdornment button': {
                border: 0,
                color: '#161C248F',
                bgcolor: 'transparent',
              },
              '& .MuiAutocomplete-endAdornment button svg': {
                color: '#161C248F',
              },
            },
          },
          { ...sx },
        ]}
        noOptionsText={<NoOptionsText />}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ],
            sx: {
              '& .MuiAutocomplete-listbox': {
                p: 0,
              },
              '& .MuiAutocomplete-noOptions': {
                py: 0,
                pr: 0.5,
              },
            },
          },
        }}
        disabled={disableAllProperties || pathname === 'point-of-sale'}
      />
      <POSConfirmationDialog
        {...{
          dialogDisclosure,
          onConfirm: async () => {
            if (!initialStateValue) return;
            setValue(initialStateValue);
            dialogDisclosure.onClose();

            setTimeout(() => {
              dispatch(setPOSSelectedStationSlice());
            }, 200);
          },
        }}
      />
    </>
  );
};
